import { Award, Briefcase, Globe, Heart, Package, Shield, Truck, Users } from "lucide-react"

export interface Service {
  id: number
  slug: string
  title: string
  shortDescription: string
  metaDescription: string
  keywords: string[]
  icon: any
  color: string
  fullDescription: string
  features: string[]
  benefits: string[]
  pricing: string
  coverage: string[]
  faq: Array<{ question: string; answer: string }>
}

export const serviceDetails: Service[] = [
  {
    id: 1,
    slug: "household-shifting",
    title: "Household Shifting",
    shortDescription: "Complete home relocation with professional packing and safe transportation of all household items.",
    metaDescription:
      "Professional household shifting services in India. Expert packing, secure transport, and insurance coverage. Get your free quote today for stress-free home relocation.",
    keywords: ["household shifting", "home relocation", "residential moving", "household packers movers", "home shifting services"],
    icon: Package,
    color: "from-blue-500 to-blue-600",
    fullDescription: `Our household shifting services are designed to make your residential relocation completely stress-free. With over 1000+ successful relocations, we provide comprehensive packing, secure transportation, and careful handling of your personal belongings. From small apartments to large villas, we handle every move with precision and care.`,
    features: [
      "Professional packing with premium materials",
      "Full insurance coverage included",
      "24/7 customer support",
      "Same-day delivery in metro cities",
      "Free damage assessment",
      "Trained and verified moving staff",
      "Furniture disassembly and reassembly",
      "Fragile items special handling",
    ],
    benefits: [
      "Zero-stress relocation experience",
      "Protected belongings with insurance",
      "Transparent pricing with no hidden costs",
      "Flexible scheduling options",
      "Eco-friendly packing materials",
      "GPS tracking of shipment",
      "Dedicated move coordinator",
      "Post-move support",
    ],
    pricing: "Starting from ₹5,000",
    coverage: ["Delhi NCR", "Mumbai", "Bangalore", "Hyderabad", "Pune", "Chennai", "Kolkata", "Ahmedabad", "Jaipur", "Lucknow"],
    faq: [
      {
        question: "How much does household shifting cost?",
        answer:
          "Costs vary based on distance, volume of items, and type of belongings. We provide free quotes after assessing your requirements. Typically, local moves start from ₹5,000.",
      },
      {
        question: "Do you provide packing materials?",
        answer: "Yes, we provide all premium packing materials including corrugated boxes, bubble wrap, tape, and protective wrapping at no extra cost.",
      },
      {
        question: "Is insurance included in household shifting?",
        answer: "Yes, basic insurance coverage is included in all our packages. Premium coverage options are also available for high-value items.",
      },
      {
        question: "How long does household shifting take?",
        answer: "Local moves typically take 1-2 days, while intercity moves take 3-7 days depending on the distance and volume of items.",
      },
    ],
  },
  {
    id: 2,
    slug: "local-shifting",
    title: "Local Shifting",
    shortDescription: "Quick and efficient local moves within the city with same-day delivery options.",
    metaDescription:
      "Fast local shifting services within the city. Same-day delivery, professional packing, and affordable rates. Book your local move today.",
    keywords: ["local shifting", "local packers movers", "same city moving", "intra-city relocation", "local home shifting"],
    icon: Truck,
    color: "from-secondary to-orange-600",
    fullDescription: `Need to move within the city? Our local shifting services offer quick, efficient, and affordable solutions for intra-city relocations. We understand the urgency of local moves and provide same-day delivery options in most metro cities. Perfect for apartment moves, office relocations, or shifting to a new neighborhood.`,
    features: [
      "Same-day delivery available",
      "Quick response time",
      "Affordable local rates",
      "Experienced local drivers",
      "Multiple vehicle options",
      "Flexible timing slots",
      "Weekend and holiday service",
      "Real-time tracking",
    ],
    benefits: [
      "Fast and efficient service",
      "Cost-effective pricing",
      "Minimal disruption",
      "Local area expertise",
      "No advance booking required",
      "Instant quote generation",
      "Damage-free guarantee",
      "Professional handling",
    ],
    pricing: "Starting from ₹3,000",
    coverage: ["All major cities", "Metro areas", "Tier-1 and Tier-2 cities"],
    faq: [
      {
        question: "Can you do same-day local shifting?",
        answer: "Yes, we offer same-day local shifting services in most metro cities. Contact us before 10 AM for same-day service availability.",
      },
      {
        question: "What is the cost of local shifting?",
        answer: "Local shifting starts from ₹3,000 depending on the volume of items and distance within the city. We provide instant quotes over phone.",
      },
      {
        question: "Do you work on weekends for local moves?",
        answer: "Yes, we operate 7 days a week including weekends and holidays for your convenience.",
      },
      {
        question: "How is local shifting different from household shifting?",
        answer: "Local shifting is specifically for moves within the same city with faster turnaround times and more affordable rates compared to intercity moves.",
      },
    ],
  },
  {
    id: 3,
    slug: "office-shifting",
    title: "Office Shifting",
    shortDescription: "Specialized corporate relocation services with minimal business downtime.",
    metaDescription:
      "Professional office shifting and corporate relocation services. Minimize downtime with our expert team. Free consultation for business moves.",
    keywords: ["office shifting", "corporate relocation", "business moving", "office packers movers", "commercial relocation"],
    icon: Briefcase,
    color: "from-purple-500 to-purple-600",
    fullDescription: `Transform your business relocations into seamless operations with our specialized office shifting services. We understand that time is money in business, which is why our corporate relocation experts handle office moves with minimal disruption to your business activities. From small startups to large enterprises, we've successfully relocated 200+ offices.`,
    features: [
      "Minimal business disruption",
      "IT infrastructure support",
      "Employee desk coordination",
      "Furniture and fixture moving",
      "Pre-move planning consultation",
      "Post-move setup assistance",
      "After-hours moving available",
      "Dedicated project manager",
    ],
    benefits: [
      "Faster business continuity",
      "Reduced downtime costs",
      "Customized moving plans",
      "Employee satisfaction",
      "Bulk discounts available",
      "Flexible timing options",
      "Professional handling",
      "Complete documentation",
    ],
    pricing: "Custom quotes based on office size",
    coverage: ["All metro cities", "Tier-1 and Tier-2 cities", "Pan-India coverage"],
    faq: [
      {
        question: "Can you move IT equipment and servers safely?",
        answer: "Yes, we have specialized teams trained in IT infrastructure relocation with proper handling, packing, and setup of servers, computers, and networking equipment.",
      },
      {
        question: "How long does office shifting take?",
        answer: "Typically 2-7 days depending on office size and complexity. We can also do after-hours or weekend moves to minimize business disruption.",
      },
      {
        question: "Do you provide office setup services?",
        answer: "Yes, we offer complete office setup including furniture assembly, IT equipment installation, and workspace organization.",
      },
      {
        question: "What about confidential documents during office move?",
        answer: "We provide secure handling of confidential documents with sealed boxes and dedicated supervision throughout the move.",
      },
    ],
  },
  {
    id: 4,
    slug: "international-moving",
    title: "International Moving",
    shortDescription: "Cross-border relocations with complete documentation and customs clearance support.",
    metaDescription:
      "International moving services with customs clearance, documentation, and door-to-door delivery. Expert team for global relocations to 50+ countries.",
    keywords: ["international moving", "overseas relocation", "international shipping", "expat moving", "global relocation"],
    icon: Globe,
    color: "from-green-500 to-green-600",
    fullDescription: `Moving abroad? Our international moving services handle all aspects of your global relocation including documentation, customs clearance, and door-to-door delivery to over 50 countries. With partnerships with leading international shipping lines and customs brokers, we ensure your belongings reach their destination safely and on time.`,
    features: [
      "Full customs documentation",
      "International shipping expertise",
      "Door-to-door delivery",
      "Air and sea freight options",
      "Export/import compliance",
      "Multilingual support",
      "Destination country assistance",
      "Pet relocation support",
    ],
    benefits: [
      "Hassle-free international relocation",
      "Expert customs handling",
      "Competitive international rates",
      "Real-time shipment tracking",
      "Visa assistance referrals",
      "Expatriate support services",
      "Insurance coverage",
      "Destination unpacking services",
    ],
    pricing: "Custom quotes based on destination",
    coverage: ["50+ countries", "All major international ports", "USA, UK, Canada, Australia, UAE, Singapore, and more"],
    faq: [
      {
        question: "What is the timeline for international moves?",
        answer: "Sea freight typically takes 4-8 weeks, while air freight takes 7-15 days depending on the destination and customs procedures.",
      },
      {
        question: "Do you handle customs documentation?",
        answer: "Yes, we manage all documentation including customs declarations, export permits, and import clearances for hassle-free international shipping.",
      },
      {
        question: "Is insurance available for international moves?",
        answer: "Yes, comprehensive marine insurance is available for all international shipments covering loss or damage during transit.",
      },
      {
        question: "Can you ship vehicles internationally?",
        answer: "Yes, we handle international vehicle shipping including cars and motorcycles with proper documentation and RoRo or container shipping options.",
      },
    ],
  },
  {
    id: 5,
    slug: "motorcycle-shifting",
    title: "Motorcycle Shifting",
    shortDescription: "Safe and secure bike transportation with specialized carriers and insurance coverage.",
    metaDescription:
      "Professional motorcycle and bike shifting services. Specialized carriers, door-to-door delivery, and full insurance. Safe bike transportation across India.",
    keywords: ["motorcycle shifting", "bike transport", "two wheeler relocation", "bike packers movers", "motorcycle carriers"],
    icon: Truck,
    color: "from-pink-500 to-pink-600",
    fullDescription: `Your motorcycle deserves special care during relocation. Our specialized motorcycle shifting services use custom carriers and protective packaging to ensure your bike reaches its destination in perfect condition. We handle all types of two-wheelers from scooters to high-end superbikes with equal care and professionalism.`,
    features: [
      "Specialized bike carriers",
      "Custom protective packaging",
      "Door-to-door pickup and delivery",
      "Full insurance coverage",
      "GPS tracking available",
      "Professional loading/unloading",
      "Fuel tank draining service",
      "Pre-move inspection report",
    ],
    benefits: [
      "Scratch-free delivery guarantee",
      "Affordable bike transport rates",
      "Quick delivery timelines",
      "Experienced bike handlers",
      "All India coverage",
      "Transparent pricing",
      "Real-time updates",
      "Damage-free guarantee",
    ],
    pricing: "Starting from ₹4,000",
    coverage: ["All major cities", "Pan-India coverage", "Remote locations accessible"],
    faq: [
      {
        question: "How much does motorcycle shifting cost?",
        answer: "Motorcycle shifting costs start from ₹4,000 for local moves and vary based on distance and bike type. High-end bikes may require premium carriers.",
      },
      {
        question: "Is my bike insured during transport?",
        answer: "Yes, all motorcycles are covered under transit insurance. We also provide additional coverage options for high-value bikes.",
      },
      {
        question: "How long does bike shifting take?",
        answer: "Local bike shifting takes 1-2 days, while intercity transport takes 3-7 days depending on the distance.",
      },
      {
        question: "Do you transport superbikes and vintage motorcycles?",
        answer: "Yes, we have specialized carriers and handling procedures for superbikes, vintage motorcycles, and high-value two-wheelers.",
      },
    ],
  },
  {
    id: 6,
    slug: "car-shifting",
    title: "Car Shifting",
    shortDescription: "Professional car transportation services with door-to-door delivery and tracking.",
    metaDescription:
      "Safe car shifting and vehicle transportation services. Door-to-door delivery, GPS tracking, and insurance coverage. Professional car carriers across India.",
    keywords: ["car shifting", "car transport", "vehicle relocation", "car carriers", "auto transportation"],
    icon: Truck,
    color: "from-indigo-500 to-indigo-600",
    fullDescription: `Relocating your car has never been easier. Our professional car shifting services use specialized car carriers with hydraulic loading systems to ensure safe transportation of your vehicle. We handle all types of cars from hatchbacks to luxury sedans and SUVs with complete care and insurance coverage.`,
    features: [
      "Enclosed and open car carriers",
      "Hydraulic loading systems",
      "Door-to-door service",
      "Comprehensive insurance",
      "GPS tracking enabled",
      "Pre and post-move inspection",
      "Professional drivers",
      "Secure transportation",
    ],
    benefits: [
      "Damage-free car delivery",
      "Competitive pricing",
      "Fast delivery timelines",
      "All India connectivity",
      "Multiple car transport options",
      "Real-time tracking",
      "Transparent process",
      "24/7 customer support",
    ],
    pricing: "Starting from ₹8,000",
    coverage: ["All major cities", "Pan-India network", "Remote locations covered"],
    faq: [
      {
        question: "How much does car shifting cost?",
        answer: "Car shifting costs start from ₹8,000 for local transport and vary based on distance, car type, and carrier type (open or enclosed).",
      },
      {
        question: "Is my car insured during transportation?",
        answer: "Yes, all vehicles are covered under comprehensive transit insurance. We also offer additional coverage for luxury and high-value cars.",
      },
      {
        question: "How long does car shifting take?",
        answer: "Local car shifting takes 1-3 days, while intercity transport takes 4-10 days depending on the distance and route.",
      },
      {
        question: "Do you transport luxury and vintage cars?",
        answer: "Yes, we have enclosed carriers specifically for luxury, vintage, and high-value cars with extra protective measures.",
      },
    ],
  },
  {
    id: 7,
    slug: "bulk-shifting",
    title: "Bulk Shifting",
    shortDescription: "Large-scale moving solutions for warehouses, factories, and bulk cargo transportation.",
    metaDescription:
      "Professional bulk shifting and cargo transportation services. Warehouse relocation, factory moves, and large-scale logistics solutions across India.",
    keywords: ["bulk shifting", "warehouse relocation", "factory moving", "bulk cargo transport", "industrial relocation"],
    icon: Package,
    color: "from-red-500 to-red-600",
    fullDescription: `Need to move large quantities of goods or relocate an entire warehouse? Our bulk shifting services are designed for large-scale relocations including warehouses, factories, retail stores, and bulk cargo transportation. With a fleet of heavy-duty vehicles and experienced logistics team, we handle projects of any size efficiently.`,
    features: [
      "Heavy-duty vehicle fleet",
      "Warehouse-to-warehouse service",
      "Industrial equipment handling",
      "Inventory management",
      "Loading dock facilities",
      "Multiple trip coordination",
      "Project management team",
      "Flexible scheduling",
    ],
    benefits: [
      "Cost-effective bulk rates",
      "Efficient large-scale moves",
      "Minimal business disruption",
      "Dedicated logistics team",
      "Customized solutions",
      "Volume-based discounts",
      "Professional handling",
      "Complete documentation",
    ],
    pricing: "Custom quotes based on volume",
    coverage: ["All major industrial areas", "Pan-India coverage", "Port-to-port services"],
    faq: [
      {
        question: "What is considered bulk shifting?",
        answer: "Bulk shifting involves moving large quantities of goods, typically warehouse relocations, factory moves, or cargo exceeding 10,000 kg.",
      },
      {
        question: "How do you calculate bulk shifting costs?",
        answer: "Costs are calculated based on total volume/weight, distance, type of goods, and special handling requirements. We provide detailed quotes after site assessment.",
      },
      {
        question: "Can you handle industrial machinery?",
        answer: "Yes, we have specialized equipment and trained personnel for handling heavy industrial machinery, production equipment, and oversized cargo.",
      },
      {
        question: "Do you provide warehouse storage during bulk moves?",
        answer: "Yes, we offer temporary warehousing solutions if you need storage between pickup and delivery locations.",
      },
    ],
  },
  {
    id: 8,
    slug: "pet-relocation",
    title: "Pet Relocation",
    shortDescription: "Specialized pet moving services with proper care, documentation, and safe transportation.",
    metaDescription:
      "Professional pet relocation services for dogs, cats, and other pets. Safe transportation, proper documentation, and veterinary support for stress-free pet moving.",
    keywords: ["pet relocation", "pet moving", "dog transport", "cat shifting", "animal transportation"],
    icon: Heart,
    color: "from-emerald-500 to-emerald-600",
    fullDescription: `Your pets are family, and we treat them that way. Our specialized pet relocation services ensure your furry friends travel safely and comfortably to their new home. We handle all documentation, provide comfortable carriers, and coordinate with veterinarians to make the journey stress-free for both you and your pets.`,
    features: [
      "IATA-approved pet carriers",
      "Veterinary coordination",
      "Pet documentation assistance",
      "Climate-controlled transport",
      "Professional pet handlers",
      "Pre-travel health checkup",
      "Quarantine assistance",
      "Door-to-door pet delivery",
    ],
    benefits: [
      "Stress-free pet travel",
      "Expert pet care",
      "Complete documentation support",
      "Safe and comfortable journey",
      "Domestic and international",
      "Real-time updates",
      "Veterinary support",
      "Compassionate handling",
    ],
    pricing: "Starting from ₹5,000",
    coverage: ["All major cities", "International pet relocation", "Remote locations accessible"],
    faq: [
      {
        question: "What documents are needed for pet relocation?",
        answer: "Required documents include vaccination records, health certificate, microchip details, and NOC from local authorities. We assist with all documentation.",
      },
      {
        question: "Is pet relocation safe?",
        answer: "Yes, we use IATA-approved carriers, provide climate-controlled transport, and have trained pet handlers to ensure your pet's safety and comfort.",
      },
      {
        question: "Can you relocate pets internationally?",
        answer: "Yes, we handle international pet relocation including all documentation, quarantine arrangements, and compliance with destination country regulations.",
      },
      {
        question: "What types of pets can you relocate?",
        answer: "We relocate dogs, cats, birds, and other domestic pets. For exotic animals, please contact us for specialized arrangements.",
      },
    ],
  },
]

export const serviceType = serviceDetails.map(service => service.title)

export const serviceStats = [
  { icon: Users, label: "500+", sublabel: "Customers", gradient: "from-blue-500 to-cyan-500" },
  { icon: Truck, label: "1000+", sublabel: "Moves", gradient: "from-green-500 to-emerald-500" },
  { icon: Shield, label: "99%", sublabel: "Safety", gradient: "from-purple-500 to-indigo-500" },
  { icon: Award, label: "4.8★", sublabel: "Rating", gradient: "from-orange-500 to-amber-500" }
]

export const ourWork = [
  { title: "Professional Packing", desc: "Premium materials & expert techniques", icon: Shield, image: "/images/work/packaging.jpeg" },
  { title: "Safe Loading", desc: "Careful handling of every item", icon: Truck, image: "/images/work/safe-loading.jpeg" },
  { title: "Secure Transport", desc: "GPS tracked & insured delivery", icon: Award, image: "/images/work/secure-transport.jpeg" }
]